import type { CrmKundenListe, Me } from '@portal/contracts';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { crmApi, HttpError } from '../../api.js';
import { navigate, onLinkClick, useSearch } from '../../router.js';
import { Problem } from '../ModulePage.js';
import { KUNDE_SEIT_UNBEKANNT, PERSON_GRUPPEN, PROTOKOLL_PFAD, crmRoute, mitFilialen, quelleAnzeige, darfBearbeiten, istNurLesen, kundenPfad } from './crm-felder.js';
import { Datensatz } from './Datensatz.js';
import { aktiveFilter, filterAusQuery, filterZuQuery, listenPfad, zahl, type CrmFilter } from './crm-kpi.js';
import { Formular } from './Formular.js';
import { useFilialen } from './useFilialen.js';
import { FilterChips, KpiLeiste, type KpiZustand } from './KpiLeiste.js';
import { Protokoll } from './Protokoll.js';
import './crm.css';

/**
 * Modul „CRM“: #815 Liste (Suche bis zum Abgleich abgeschaltet), #830 Datensatz `/crm/kunden/:id`, Anlegen (nur ab Stufe
 * bearbeiten, aus /api/me.modules.crm) und Änderungsprotokoll `/crm/protokoll`. Texte in Sie-Form. Versionen sind nur lesbar.
 * #839: KPI-Leiste über der Liste; Klick auf einen Kachelwert setzt den Listenfilter in der URL-Query (teilbar), aktive
 * Filter als entfernbare Chips.
 */
const DATUM = new Intl.DateTimeFormat('de-DE', { dateStyle: 'medium', timeZone: 'Europe/Berlin' });

type Laden = { kind: 'laden' } | { kind: 'fehler'; status: number; text: string | null } | { kind: 'ok'; daten: CrmKundenListe };

export function Crm({ me, pathname }: { me: Me; pathname: string }) {
  const route = crmRoute(pathname);
  if (route.seite === 'kunde') return <Datensatz key={route.id} id={route.id} me={me} />;
  if (route.seite === 'protokoll') return <Protokoll />;
  if (route.seite === 'unbekannt') return <Problem status={404} />;
  return <Liste me={me} />;
}

function Liste({ me }: { me: Me }) {
  const [state, setState] = useState<Laden>({ kind: 'laden' });
  const [kpi, setKpi] = useState<KpiZustand>({ kind: 'laden' });
  const [anlegen, setAnlegen] = useState(false);
  // #851 Stammfiliale immer als Auswahl (kunden.filiale); geladen beim Öffnen von „Kunde anlegen“
  const filialen = useFilialen(anlegen);
  const schreiben = darfBearbeiten(me);
  const sucheId = useId();
  const hinweisId = useId();
  const search = useSearch();
  const schluessel = filterZuQuery(filterAusQuery(search));
  const filter = useMemo<CrmFilter>(() => filterAusQuery(schluessel), [schluessel]);

  // Nur die jüngste Anfrage gewinnt (schnelle Filterwechsel dürfen keine ältere Antwort anzeigen)
  const anfrage = useRef(0);
  const laden = useCallback(async () => {
    const nr = ++anfrage.current;
    try {
      const daten = await crmApi.kunden(1, 50, filter);
      if (nr === anfrage.current) setState({ kind: 'ok', daten });
    } catch (err) {
      if (nr !== anfrage.current) return;
      const status = err instanceof HttpError ? err.status : 0;
      setState({ kind: 'fehler', status, text: err instanceof HttpError ? err.text : null });
    }
  }, [filter]);
  useEffect(() => void laden(), [laden]);

  const kpiLaden = useCallback(async () => {
    setKpi({ kind: 'laden' });
    try {
      setKpi({ kind: 'ok', daten: await crmApi.kpi() });
    } catch (err) {
      setKpi({ kind: 'fehler', text: err instanceof HttpError ? err.text : null });
    }
  }, []);
  useEffect(() => void kpiLaden(), [kpiLaden]);

  // Keine Berechtigung / Rechteprüfung nicht erreichbar / Sitzung abgelaufen → Problem-Seite der Shell
  if (state.kind === 'fehler' && (state.status === 403 || state.status === 401 || state.status === 503 || state.status === 404)) {
    return <Problem status={state.status} />;
  }

  return (
    <section className="crm" aria-labelledby="crm-titel">
      <div className="ap-head">
        <div>
          <p className="crm-crumb">CRM</p>
          <h1 className="ap-title" id="crm-titel">
            CRM
          </h1>
          <p className="ap-sub">Kundenstamm des Portals</p>
        </div>
        <div className="crm-kopf-aktionen">
          <a className="pb-button pb-button--quiet" href={PROTOKOLL_PFAD} onClick={onLinkClick(PROTOKOLL_PFAD)}>
            Änderungsprotokoll
          </a>
          {schreiben && !anlegen && (
            <button type="button" className="pb-button" onClick={() => setAnlegen(true)}>
              Kunde anlegen
            </button>
          )}
        </div>
      </div>

      {anlegen && (
        <div className="pb-card crm-karte">
          <h2>Kunde anlegen</h2>
          <p className="crm-muted">Quelle „Portal“. Alle Felder sind optional; Adressen, Kontaktkanäle und Einwilligungen folgen im Datensatz.</p>
          <Formular
            defs={mitFilialen(PERSON_GRUPPEN.flatMap((g) => g.felder).filter((f) => !istNurLesen(f)), filialen.zustand, null, filialen.erneut)}
            original={{}}
            neu
            titel="Kunde anlegen"
            onSpeichern={async (patch, kommentar) => {
              const p = await crmApi.anlegen(kommentar ? { ...patch, kommentar } : patch);
              navigate(kundenPfad(p.id));
            }}
            onAbbrechen={() => setAnlegen(false)}
          />
        </div>
      )}

      <KpiLeiste zustand={kpi} filter={filter} onErneut={() => void kpiLaden()} />

      <div className="crm-suche">
        <label htmlFor={sucheId}>Name oder E-Mail suchen</label>
        <input id={sucheId} type="search" disabled aria-describedby={hinweisId} autoComplete="off" placeholder="Suche" />
        <span id={hinweisId} className="crm-muted">
          Suche folgt mit dem Abgleich.
        </span>
      </div>

      <FilterChips filter={filter} />

      {state.kind === 'laden' ? (
        <p className="sh-status" aria-busy="true">
          Kundenstamm wird geladen …
        </p>
      ) : state.kind === 'fehler' ? (
        <div className="pb-card crm-hinweis crm-hinweis--fehler" role="alert">
          <p>{state.text ?? 'Der Kundenstamm konnte nicht geladen werden. Bitte versuchen Sie es erneut.'}</p>
          <div className="crm-kopf-aktionen">
            <button type="button" className="pb-button pb-button--quiet" onClick={() => void laden()}>
              Erneut versuchen
            </button>
            {aktiveFilter(filter).length > 0 && (
              <a className="pb-button pb-button--quiet" href={listenPfad({})} onClick={onLinkClick(listenPfad({}))}>
                Alle Filter entfernen
              </a>
            )}
          </div>
        </div>
      ) : (
        <Tabelle daten={state.daten} gefiltert={aktiveFilter(filter).length > 0} />
      )}
    </section>
  );
}

function Tabelle({ daten, gefiltert }: { daten: CrmKundenListe; gefiltert: boolean }) {
  const leer = daten.eintraege.length === 0;
  return (
    <div className={`pb-card crm-tabelle-wrap${leer ? ' crm-tabelle-wrap--leer' : ''}`}>
      <table className="crm-tabelle">
        <caption className="sr-only">Kundenstamm mit Name, E-Mail, Quelle, Kunde seit und Stand{gefiltert ? ' (gefiltert)' : ''}</caption>
        <thead>
          <tr>
            <th scope="col">Name</th>
            <th scope="col">E-Mail</th>
            <th scope="col">Quelle</th>
            <th scope="col">Kunde seit</th>
            <th scope="col">Stand</th>
          </tr>
        </thead>
        <tbody>
          {leer ? (
            <tr className="crm-leer">
              <td colSpan={5}>{gefiltert ? 'Keine Personen für diese Filter.' : 'Noch keine Kunden. Der Abgleich mit Shopify und NEO folgt.'}</td>
            </tr>
          ) : (
            daten.eintraege.map((k) => (
              <tr key={k.id} className="crm-zeile-link" onClick={() => navigate(kundenPfad(k.id))}>
                <td data-label="Name" className="crm-name">
                  <a href={kundenPfad(k.id)} onClick={onLinkClick(kundenPfad(k.id))}>
                    {k.name ?? 'Ohne Namen'}
                  </a>
                </td>
                <td data-label="E-Mail" className="crm-mail">
                  {k.email ?? '–'}
                </td>
                <td data-label="Quelle">
                  {quelleAnzeige(k)}
                  {k.abgleichPruefen && (
                    <>
                      {' '}
                      <span className="crm-badge crm-badge--warn">Abgleich prüfen</span>
                    </>
                  )}
                </td>
                <td data-label="Kunde seit" className="pb-num">
                  {k.kundeSeit ? (
                    DATUM.format(new Date(k.kundeSeit))
                  ) : (
                    <span className="crm-muted crm-unbekannt" title={KUNDE_SEIT_UNBEKANNT}>
                      unbekannt<span className="sr-only"> ({KUNDE_SEIT_UNBEKANNT})</span>
                    </span>
                  )}
                </td>
                <td data-label="Stand" className="pb-num">
                  {DATUM.format(new Date(k.geaendertAt))}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {!leer && (
        <p className="crm-fuss crm-muted" role="status">
          {zahl(daten.gesamt)} {daten.gesamt === 1 ? 'Person' : 'Personen'}
          {gefiltert ? ' mit diesen Filtern' : ''} · Seite {daten.seite}
          {daten.gesamt > daten.eintraege.length ? ` (${zahl(daten.eintraege.length)} angezeigt)` : ''}
        </p>
      )}
    </div>
  );
}
